import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type RefObject,
} from 'react';

import styles from './Dialog.module.css';
import { DEFAULT_HUE, type Hue } from './hue';
import { Icon, type IconName } from './Icon';
import { IconButton } from './IconButton';

/**
 * A modal dialog — docs/DESIGN.md §6.7, §7.4, ADR-0101 §7.11.
 *
 * Native `<dialog>` + `showModal()`, because the top layer, the inert background and the
 * `::backdrop` pseudo-element come for free and are correct in a way a hand-built overlay
 * never quite is.
 *
 * THE FOCUS TRAP IS EXPLICIT AND NOT INHERITED. `showModal()` does make the rest of the page
 * inert in a real browser, but "inert" is not the same as "Tab cycles inside the dialog": the
 * focus can still walk out into the browser's own UI, and jsdom implements none of it — so the
 * legacy dialog's complete absence of a trap would have gone on being invisible in tests. The
 * trap below runs in the CAPTURE phase so no inner handler can swallow the Tab first, and
 * `Dialog.test.tsx` asserts the cycle in both directions.
 *
 * The three keyboard/focus contracts, all tested:
 *   · focus moves into the dialog on open (to `initialFocus` if given, else the first
 *     focusable, else the panel itself);
 *   · `Escape` closes — and so does a click on the scrim;
 *   · focus returns to whatever opened it. Losing focus to `<body>` after a dialog closes
 *     dumps a keyboard user back at the top of the document.
 *
 * `ui/**` does not translate: `title`, `closeLabel` and the footer arrive localised.
 *
 * ═══ ON A PHONE IT IS A SHEET, AND THAT IS A STYLESHEET DECISION ═══
 *
 * ADR-0101 §7.11 turns the panel into a bottom sheet below 600px: full width, rounded at the
 * top, a grabber instead of a footer. Not one line of that is here — `margin` does it in
 * `Dialog.module.css`. The only thing this file owes the sheet is the grabber element and the
 * `display: none` footer's consequence for the focus trap, both handled below.
 */

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Deliberately does NOT filter on `offsetParent` the way the mockup's trap does. That test is
 * a layout read, and jsdom has no layout: every element would be judged invisible and the trap
 * would quietly do nothing in exactly the environment where it is tested.
 *
 * `display: none` IS filtered, and through the computed style rather than through layout,
 * because ADR-0101 §7.11 hides `.foot` — and with it a Cancel button — below 600px. A trap
 * that still counted that button would name it `last`, and `last.focus()` on an undisplayed
 * element does nothing at all: the user would tab off the final row, out of the dialog, and
 * only be caught on the NEXT keypress. `getComputedStyle` needs no layout, so it is honest in
 * both environments: a real browser reports `none` for the hidden footer, and jsdom — which
 * applies no stylesheet at all under CSS Modules — reports the initial value for everything
 * and the trap keeps seeing the whole dialog, exactly as `Dialog.test.tsx` expects.
 */
function isDisplayed(el: HTMLElement, root: HTMLElement): boolean {
  const view = el.ownerDocument.defaultView;
  if (view === null) return true;
  // `display: none` does not inherit and is not reported by a child's computed style, so the
  // chain between the candidate and the dialog has to be walked.
  for (let node: HTMLElement | null = el; node !== null; node = node.parentElement) {
    if (view.getComputedStyle(node).display === 'none') return false;
    if (node === root) break;
  }
  return true;
}

function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      !el.hidden &&
      el.closest('[hidden]') === null &&
      el.getAttribute('aria-hidden') !== 'true' &&
      isDisplayed(el, root),
  );
}

export interface DialogProps {
  readonly open: boolean;
  /** Asked to close: Escape, the × control, or a click on the scrim. The owner sets state. */
  readonly onClose: () => void;
  readonly title: string;
  /** Accessible name of the × control. */
  readonly closeLabel: string;
  /** One sentence under the title — the privacy promise, the consequence of loading. */
  readonly description?: ReactNode;
  readonly children: ReactNode;
  /** The keyboard contract row: `↑↓ move`, `⏎ load`, a Cancel button. */
  readonly footer?: ReactNode;
  /** Where focus lands on open. Defaults to the first focusable element in the dialog. */
  readonly initialFocus?: RefObject<HTMLElement | null>;
  /**
   * The family this dialog speaks in — ADR-0101 §1, §7.11. It dresses the head tile, the wash
   * under the head and the halo, and it reaches the DOM as `data-hue` so that CSS Modules'
   * hashing cannot rename it and so that children inherit the whole `--h*` recipe.
   */
  readonly hue?: Hue;
  /** The glyph in the head tile. Decoration with a meaning; the title says the same in words. */
  readonly icon?: IconName;
  readonly className?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  closeLabel,
  description,
  children,
  footer,
  initialFocus,
  hue = DEFAULT_HUE,
  icon,
  className,
}: DialogProps): ReactElement {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    const el = ref.current;
    if (el === null || !open) return undefined;

    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!el.open) el.showModal();

    const initial = initialFocus?.current ?? focusableWithin(el)[0] ?? el;
    initial.focus();

    return () => {
      if (el.open) el.close();
      // Restoration, not a courtesy: without it a keyboard user is returned to <body> and has
      // to tab from the top of the page back to the control they just used.
      opener.current?.focus();
      opener.current = null;
    };
  }, [open, initialFocus]);

  const onKeyDownCapture = useCallback(
    (event: ReactKeyboardEvent<HTMLDialogElement>) => {
      const el = ref.current;
      if (el === null) return;

      if (event.key === 'Escape') {
        // Handled here rather than left to the native `cancel` event so that the owner's state
        // and the element's `open` property can never disagree.
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = focusableWithin(el);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) {
        // Nothing to move to: keep the focus where it is rather than letting it leave.
        event.preventDefault();
        return;
      }

      const active = document.activeElement;
      if (event.shiftKey && (active === first || !el.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !el.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  // A click that lands on the <dialog> element itself — not on any of its children — is a
  // click on the scrim (docs/DESIGN.md §6.7).
  const onClick = useCallback(
    (event: ReactMouseEvent<HTMLDialogElement>) => {
      if (event.target === ref.current) onClose();
    },
    [onClose],
  );

  return (
    <dialog
      ref={ref}
      className={className === undefined ? styles.dialog : `${styles.dialog} ${className}`}
      // Explicit border under Windows High Contrast, where the tone step collapses (base.css).
      data-surface="dialog"
      data-hue={hue}
      aria-labelledby={titleId}
      {...(description !== undefined && { 'aria-describedby': descriptionId })}
      onKeyDownCapture={onKeyDownCapture}
      onClick={onClick}
      onCancel={(event) => {
        // Belt and braces for the native Escape path in engines that reach it first.
        event.preventDefault();
        onClose();
      }}
    >
      {/* Furniture, drawn only on the phone sheet. The × is the close control; this is the
          affordance that says "this thing came up from the bottom edge". */}
      <span className={styles.grab} aria-hidden="true" />

      <div className={styles.head}>
        {icon === undefined ? null : (
          <span className={styles.tile} aria-hidden="true">
            <Icon name={icon} size={18} />
          </span>
        )}
        <h2 className={styles.title} id={titleId}>
          {title}
        </h2>
        <IconButton icon="close" label={closeLabel} onClick={onClose} />
      </div>

      {description === undefined ? null : (
        <p className={styles.description} id={descriptionId}>
          {description}
        </p>
      )}

      <div className={styles.body}>{children}</div>

      {footer === undefined ? null : <div className={styles.foot}>{footer}</div>}
    </dialog>
  );
}

export default Dialog;
